// File: src/components/general/Tooltip/Tooltip.tsx
// Purpose: General UI component or utility

import React, { ReactNode, useState } from "react";
import styled, { css } from "styled-components";
import { AppTheme } from "theme";

interface TooltipProps {
  content: string | ReactNode;
  position?: "top" | "right" | "bottom" | "left";
  children: ReactNode;
  compact?: boolean; // ✅ Compact mode for mobile
}

const TooltipWrapper = styled.div`
  position: relative;
  
  display: inline-flex;
  align-items: center;
`;

const TooltipBubble = styled.div<{ position: string }>`
  position: absolute;
    z-index: 1500; /* 🔥 Higher than sidebar */

  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.buttonText};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.radii.md};
  white-space: nowrap;
  z-index: 10;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  pointer-events: none;

  ${({ position }) =>
    position === "top"
      ? `bottom: 100%; left: 50%; transform: translateX(-50%) translateY(-4px);`
      : position === "right"
      ? `left: 100%; top: 50%; transform: translateY(-50%) translateX(4px);`
      : position === "bottom"
      ? `top: 100%; left: 50%; transform: translateX(-50%) translateY(4px);`
      : `right: 100%; top: 50%; transform: translateY(-50%) translateX(-4px);`}

  &::before {
  content: "";
  position: absolute;
  border: 6px solid transparent;

  ${({ position, theme }: { position?: any; theme: AppTheme }) => {
    switch (position) {
      case "top":
        return css`
          bottom: -12px;
          left: 50%;
          transform: translateX(-50%);
          border-top-color: ${theme.colors.textPrimary};
        `;
      case "right":
        return css`
          left: -12px;
          top: 50%;
          transform: translateY(-50%);
          border-right-color: ${theme.colors.textPrimary};
        `;
      case "bottom":
        return css`
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          border-bottom-color: ${theme.colors.textPrimary};
        `;
      default:
        return css`
          right: -12px;
          top: 50%;
          transform: translateY(-50%);
          border-left-color: ${theme.colors.textPrimary};
        `;
    }
  }}
}
`;

const TooltipTrigger = styled.div`
  &:hover + ${TooltipBubble}, &:focus + ${TooltipBubble} {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Tooltip: React.FC<TooltipProps> = ({ content, position = "top", children, compact }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <TooltipWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={() => setIsHovered(false)}
    >
      <TooltipTrigger>{children}</TooltipTrigger>
      {isHovered && <TooltipBubble position={position}>{content}</TooltipBubble>}
    </TooltipWrapper>
  );
};